import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, FileText, Upload, Calendar, Download } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'rapport', label: 'Rapport d\'activité' },
  { id: 'manifeste', label: 'Manifeste & Vision' },
  { id: 'etude', label: 'Étude / Recherche' },
  { id: 'brochure', label: 'Brochure' },
  { id: 'presentation', label: 'Présentation' },
  { id: 'autre', label: 'Autre' },
];

const EMPTY_FORM = {
  title: '',
  description: '',
  category: 'rapport',
  year: new Date().getFullYear(),
  file_url: '',
  file_size: 0,
  original_filename: ''
};

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

export default function DocumentsManager() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchDocs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/documents`);
      setDocs(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 30 * 1024 * 1024) {
      toast.error('Le fichier ne doit pas dépasser 30 Mo');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/files/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({
        ...formData,
        file_url: res.data.url,
        file_size: file.size,
        original_filename: file.name,
        title: formData.title || file.name.replace(/\.[^.]+$/, '')
      });
      toast.success('Fichier chargé');
    } catch (error) { toast.error('Erreur lors du chargement'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.file_url) {
      toast.error('Veuillez charger un fichier');
      return;
    }
    try {
      if (editingId) {
        await axios.put(`${API}/admin/documents/${editingId}`, formData, { withCredentials: true });
        toast.success('Document mis à jour');
      } else {
        await axios.post(`${API}/admin/documents`, formData, { withCredentials: true });
        toast.success('Document ajouté');
      }
      resetForm();
      fetchDocs();
    } catch (error) { toast.error('Erreur'); }
  };

  const handleEdit = (doc) => {
    setEditingId(doc.document_id);
    setFormData({
      title: doc.title,
      description: doc.description || '',
      category: doc.category,
      year: doc.year || new Date().getFullYear(),
      file_url: doc.file_url,
      file_size: doc.file_size || 0,
      original_filename: doc.original_filename || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await axios.delete(`${API}/admin/documents/${id}`, { withCredentials: true });
      toast.success('Document supprimé');
      fetchDocs();
    } catch (error) { toast.error('Erreur'); }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  const resolveUrl = (url) => (url && url.startsWith('http')) ? url : `${BACKEND_URL}${url}`;

  return (
    <div data-testid="documents-manager">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Documents & Rapports ({docs.length})
        </h2>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary inline-flex items-center" data-testid="add-document-button">
          <Plus size={20} className="mr-2" />
          {showForm ? 'Annuler' : 'Ajouter un document'}
        </button>
      </div>

      <p className="text-sm text-[#5C665A] mb-4 italic">
        Téléversez ici tous types de fichiers publics : rapports d&apos;activité, manifeste, études, brochures, présentations.
        Formats acceptés : PDF, DOCX, XLSX, PPTX (max 30 Mo).
      </p>

      {showForm && (
        <form onSubmit={handleSubmit} className="card mb-6" data-testid="document-form">
          <div className="space-y-4">
            <div className="border-2 border-dashed border-[#E5E3DB] rounded-lg p-4">
              <label className="flex items-center gap-2 text-sm font-bold text-[#2E332D] mb-2">
                <Upload size={16} /> Fichier (max 30 Mo)
              </label>
              <input
                ref={fileInputRef} type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                onChange={handleUpload} disabled={uploading}
                className="text-sm" data-testid="document-upload"
              />
              {uploading && <p className="text-xs text-[#5C665A] mt-1">Chargement...</p>}
              {formData.file_url && (
                <div className="mt-2 flex items-center justify-between bg-[#F9F8F6] p-2 rounded">
                  <span className="text-sm text-[#2E332D] inline-flex items-center gap-1">
                    <FileText size={14} className="text-[#4A6B48]" />
                    {formData.original_filename} {formData.file_size > 0 && `· ${formatSize(formData.file_size)}`}
                  </span>
                  <button type="button" onClick={() => setFormData({ ...formData, file_url: '', file_size: 0, original_filename: '' })} className="text-xs text-red-500 hover:underline">Retirer</button>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-bold text-[#2E332D] mb-1">Titre du document *</label>
              <input
                type="text" required value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                data-testid="document-title-input"
                placeholder="Ex: Rapport d'activité 2025"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-[#2E332D] mb-1">Description (optionnel)</label>
              <textarea
                rows={3} value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                data-testid="document-description-input"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-[#2E332D] mb-1">Catégorie *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                  data-testid="document-category-select"
                >
                  {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-[#2E332D] mb-1">Année</label>
                <input
                  type="number" value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value, 10) || null })}
                  className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                  data-testid="document-year-input"
                  min="2000" max="2100"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button type="submit" className="btn-primary" data-testid="submit-document-button">
                {editingId ? 'Mettre à jour' : 'Ajouter'}
              </button>
              <button type="button" onClick={resetForm} className="btn-secondary">Annuler</button>
            </div>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4A6B48]"></div></div>
      ) : docs.length === 0 ? (
        <p className="text-center text-[#5C665A] py-8">Aucun document. Cliquez sur &ldquo;Ajouter un document&rdquo;.</p>
      ) : (
        <div className="space-y-3">
          {docs.map((doc) => (
            <div key={doc.document_id} className="card" data-testid={`admin-document-${doc.document_id}`}>
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-xs font-bold px-2 py-0.5 bg-[#4A6B48]/10 text-[#4A6B48] rounded">
                      {CATEGORIES.find((c) => c.id === doc.category)?.label || doc.category}
                    </span>
                    {doc.year && (
                      <span className="text-xs text-[#5C665A] inline-flex items-center gap-1">
                        <Calendar size={12} /> {doc.year}
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-bold text-[#2E332D] inline-flex items-center gap-2">
                    <FileText size={18} className="text-[#4A6B48]" />
                    {doc.title}
                  </h3>
                  {doc.description && <p className="text-sm text-[#5C665A] mt-1 line-clamp-2">{doc.description}</p>}
                  <p className="text-xs text-[#5C665A] mt-2">
                    {doc.original_filename}
                    {doc.file_size > 0 && ` · ${formatSize(doc.file_size)}`}
                    {typeof doc.download_count === 'number' && doc.download_count > 0 && (
                      <span className="ml-2 text-[#4A6B48] font-bold">
                        · {doc.download_count} téléchargement{doc.download_count > 1 ? 's' : ''}
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  <a
                    href={resolveUrl(doc.file_url)} target="_blank" rel="noreferrer"
                    className="p-2 hover:bg-gray-100 rounded" title="Télécharger"
                  >
                    <Download size={18} className="text-[#4A6B48]" />
                  </a>
                  <button onClick={() => handleEdit(doc)} className="p-2 hover:bg-gray-100 rounded">
                    <Edit2 size={18} className="text-[#4A6B48]" />
                  </button>
                  <button onClick={() => handleDelete(doc.document_id)} className="p-2 hover:bg-gray-100 rounded">
                    <Trash2 size={18} className="text-red-500" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
